// Shared graph-canvas math for the Tech Tree editor.
//
// The tech tree lives on a plain square grid, unlike the isometric Map Editor
// and Workshop — so the projection is defined here. The *camera* model is the
// same one all three canvases use and is imported from isoCanvas rather than
// duplicated, so wheel zoom, pinch and the zoom clamps behave identically
// everywhere. Re-exported below so the tech tree has a single import.
import {
    clampZoom,
    DEFAULT_VIEW,
    ViewTransform,
    ZOOM_MAX,
    ZOOM_MIN,
    ZOOM_PER_PINCH,
    ZOOM_PER_WHEEL,
} from "./isoCanvas";

export {clampZoom, DEFAULT_VIEW, ZOOM_MAX, ZOOM_MIN, ZOOM_PER_PINCH, ZOOM_PER_WHEEL};
export type {ViewTransform};

// One grid cell in world px at zoom 1. Deliberately finer than a node: a circle
// is NODE_R * 2 = 90 across and so spans about a cell and a half, which lets a
// node be nudged into place at sub-node granularity.
//
// The trade is that two nodes on adjacent cells overlap. That's accepted — only
// dropping onto an already-occupied cell is rejected, so fine positioning stays
// possible and it's the designer's call whether circles touch.
export const CELL = 60;
export const NODE_R = 45;

// The smallest gap, in cells, that keeps two node circles from overlapping.
// Derived from CELL and NODE_R so placement heuristics track them instead of
// restating the relationship as magic numbers.
export const MIN_CELL_GAP = Math.max(1, Math.ceil((NODE_R * 2) / CELL));

export function cellToWorld(x: number, y: number): [number, number] {
    return [x * CELL, y * CELL];
}

export function snapToCell(wx: number, wy: number): {x: number; y: number} {
    return {x: Math.round(wx / CELL), y: Math.round(wy / CELL)};
}

// Grid cells are the identity for collision and for "did this actually move",
// so they're compared as strings in a Set rather than by object equality.
export function cellKey(x: number, y: number): string {
    return `${x},${y}`;
}

// Level of detail for a node's contents. Keyed on the node's radius in SCREEN
// pixels, not on zoom: the question is only ever "does this still fit inside
// the circle", so the thresholds stay correct if CELL or NODE_R ever change.
export type NodeDetail = 'dot' | 'marker' | 'name' | 'full';

// Thresholds are screen radii in px. Node text scales with the circle, so the
// name tier starts where NAME_FONT_RATIO * NODE_R * zoom is still legible
// (~6px) — below that the label would be a grey smudge and the marker reads
// better. Tune these together with NAME_FONT_RATIO.
export function nodeDetail(zoom: number): NodeDetail {
    const r = NODE_R * zoom;
    if (r < 9) return 'dot';        // circle only
    if (r < 24) return 'marker';    // circle + unlock-count marker
    if (r < 44) return 'name';      // circle + truncated name
    return 'full';                  // circle + name + unlock icons
}

// Name label size as a fraction of the node radius. Kept here next to the LOD
// thresholds because the two have to agree on what "legible" means: the name
// tier starts at the screen radius where this ratio still yields ~6px of text.
export const NAME_FONT_RATIO = 0.255;

// Roughly how many characters fit across a circle of this radius, for
// truncating the name label. Both arguments are in the same units (world px),
// so the budget is zoom-independent: the label always fills the same fraction
// of the circle, and LOD above decides whether it's drawn at all. 0.55 is the
// average advance width of the UI font relative to its size.
export function nameCharBudget(radius: number, fontSize: number): number {
    return Math.max(3, Math.floor((radius * 1.7) / (fontSize * 0.55)));
}
